import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser, getUserHashId } from "store/userSlice";
import * as Sentry from "@sentry/react";
import { removeCsrfCookie } from "helpers/cookie";
import logo from "assets/images/peb-logo.svg";
import { fetchPeriods, getActivePeriod, getPeriods } from "store/periodSlice";
import ErrorAlert from "components/ui/ErrorAlert";
import { initBuglogForAuthenticatedUser } from "helpers/buglog";
import { useTranslation } from "translation/useTranslation";
import { Button, Field, NativeSelect } from "components/ui";

export default function PeriodSelector() {
  const dispatch = useDispatch();
  const buglogInitAttempted = useRef(false);

  const { setValue, getValues } = useForm();

  const { translate, editableTranslate } = useTranslation();

  const userHashId = useSelector(getUserHashId);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [defaultSelected, setDefaultSelected] = useState('');

  const periods = useSelector(getPeriods);
  const activePeriod = useSelector(getActivePeriod);

  useEffect(() => {
    if (buglogInitAttempted.current) {
      return;
    }

    buglogInitAttempted.current = true;

    async function verifyAuthAndInitBuglog() {
      try {
        if (!userHashId) {
          await dispatch(fetchUser()).unwrap();
        }

        initBuglogForAuthenticatedUser();
      } catch {
        // Do not initialize Buglog unless auth is verified.
      }
    }

    verifyAuthAndInitBuglog();
  }, [dispatch, userHashId]);

  useEffect(() => {
    dispatch(fetchPeriods())
  }, []);

  useEffect(() => {
    if (periods && periods.length > 0) {
      // set default selected period to the first one if no period is active
      setDefaultSelected(periods[0].name);
      setValue('period', periods[0].name);
    }
  }, [periods]);

  const handleSelectPeriod = async () => {
    const period = getValues('period');

    // do nothing if period is already active
    if (period === activePeriod) return;

    setLoading(true);
    const currentPeriod = activePeriod;

    try {
      const { data, status } = await api.post(`/common/periods/init-switch`, {
        period: period
      });

      if (status === 200) {
        try {
          // reset cookie and set new period
          removeCsrfCookie();
          localStorage.setItem('period', data.data.p);
          api.defaults.headers['X-Period'] = data.data.p;
          const { status } = await api.post(`/common/periods/switch`, {
            uc: data.data.uc,
          });

          if (status === 200) {
            window.location.href = '/employee-management?with=changelogs';
          }
        } catch (err) {
          localStorage.setItem('period', currentPeriod);
          throw err;
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'switch period'));
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container" data-testid="period-selector">
      <div className="login-div">
        <div className="text-center">
          <img src={logo} alt={translate('global.software_name', 'PEB')} height="48px" />
        </div>
        <div className="mt-20px">
          <div className="alert alert-warning" role="alert" data-testid="period-selector-warning">
            <strong>Note: </strong><br />
            {editableTranslate('global.period_selector_info', 'Sie haben keinen Zugriff auf die aktuelle Arbeitsperiode. Bitte wählen Sie eine andere Periode aus, um fortzufahren.')}
          </div>
          {
            error && <ErrorAlert error={error} onClose={() => setError(null)} />
          }
          <div className="form-group mb-0">
            <Field label={editableTranslate('global.period_selector', 'Periode auswählen')} htmlFor="period">
              <div className="input-group">
                <NativeSelect id="period" name="period" onChange={(e) => setValue('period', e.target.value)} disabled={loading} defaultValue={defaultSelected} data-testid="period-select">
                  {
                    periods && periods.map((period, i) => (
                      <option key={i} value={period.name} data-testid={`period-option-${period.name}`}>{period.name}</option>
                    ))
                  }
                </NativeSelect>
                <div className="input-group-append">
                  <Button variant="primary" onClick={handleSelectPeriod} loading={loading} data-testid="confirm-button">{!loading && <i className="bi bi-arrow-right bi-bold"></i>}</Button>
                </div>
              </div>
            </Field>
          </div>
        </div >
      </div>
    </div >
  );
}
